import React, { useState, useEffect } from "react";
import {
  Box,
  Input,
  VStack,
  Text,
  Button,
  SimpleGrid,
  Flex,
  IconButton,
  useToast,
  Icon,
  HStack,
  Heading,
  Stack,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Container,
  InputGroup,
  InputLeftElement,
  Card,
  CardBody,
  useDisclosure,
} from "@chakra-ui/react";
import {
  DashboardCategoryPanel,
  DashboardFolderCard,
  DashboardActionShell,
} from "../utils/DashboardCategoryPanel";
import {
  DashboardDataTable,
  useDashboardTableStyles,
} from "../utils/DashboardDataTable";
import axios from "axios";
import { CloseIcon, AddIcon, CheckIcon, EditIcon } from "@chakra-ui/icons";
import { hitBeApi } from "../../../api/api";
import TabCacheButton from "../../common/TabCacheButton";
import { getCookieToken } from "../../../utils/utils";
import { serverBaseUrl } from "../../../constants/Constants";
import { MdCloudUpload } from "react-icons/md";
import { FiFolder, FiSearch } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import DeleteButtonWithConfirmation from "../utils/DeleteConfirmation";
import ShareCollaborationModal from "../../common/ShareCollaborationModal";

const FileManager = () => {
  const toast = useToast();
  const shareFolderModal = useDisclosure();
  const [shareCategoryTarget, setShareCategoryTarget] = useState(null);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [files, setFiles] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [subCategories, setSubCategories] = useState([]);
  const [subFiles, setSubFiles] = useState([]);
  const [newItem, setNewItem] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const [fileURL, setFileURL] = useState(""); // State for file URL
  const [fileName, setFileName] = useState(""); // State for file URL
  const navigate = useNavigate();
  const [isCategoryBeingSelected, setIsCategoryBeingSelected] = useState(false);

  const [isEditing, setIsEditing] = useState(null); // To track which category or URL is being edited
  const [editedCategoryName, setEditedCategoryName] = useState("");
  const [editedFileName, setEditedFileName] = useState("");
  const [editedFileUrl, setEditedFileUrl] = useState("");
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [categoryPath, setCategoryPath] = useState([]);

  // Color mode values for light and dark mode support
  const boxBgColor = useColorModeValue("white", "gray.600");
  const uploadBgColor = useColorModeValue("teal.50", "teal.900");
  const uploadHoverBgColor = useColorModeValue("teal.100", "teal.700");
  const uploadTextColor = useColorModeValue("teal.700", "teal.200");
  const inputFocusBorderColor = useColorModeValue("teal.500", "teal.300");
  const inputFocusBoxShadow = useColorModeValue(
    "0 0 0 1px teal.500",
    "0 0 0 1px teal.300"
  );
  // Move all useColorModeValue calls to the top
  const focusBorderColor = useColorModeValue("blue.500", "blue.300");
  const linkColor = useColorModeValue("blue.600", "blue.300");
  const hoverLinkColor = useColorModeValue("blue.400", "blue.200");
  // Define color mode values for various properties
  const inputBg = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("teal.400", "teal.200");
  const hoverBorderColor = useColorModeValue("teal.500", "teal.300");
  const focusBoxShadow = useColorModeValue(
    "0 0 0 1px teal.600",
    "0 0 0 1px teal.400"
  );
  const categoryRowBg = useColorModeValue("white", "gray.800");
  const categoryRowHoverBg = useColorModeValue("gray.50", "gray.700");
  const categoryIconBgRoot = useColorModeValue("teal.50", "teal.900");
  const categoryIconBgNested = useColorModeValue("blue.50", "blue.900");
  const textColorCard = useColorModeValue("gray.800", "gray.100");
  const headingCategoryColor = useColorModeValue("blue.600", "blue.300");
  const tableStyles = useDashboardTableStyles();

  const pageBg = useColorModeValue("gray.50", "gray.900");
  const surfaceCard = useColorModeValue("white", "gray.800");
  const mutedLine = useColorModeValue("gray.200", "gray.600");
  const subtextColor = useColorModeValue("gray.600", "gray.400");
  const searchPanelBg = useColorModeValue("white", "gray.800");
  const breadcrumbBg = useColorModeValue("gray.50", "gray.900");
  const categoryFolderAccent = useColorModeValue("teal.600", "teal.300");
  const categoryNestedAccent = useColorModeValue("gray.600", "gray.400");

  const handleStartEdit = (item, type) => {
    setIsEditing({ id: item._id, type });
    if (type === "category") {
      setEditedCategoryName(item.name);
    } else {
      setEditedFileName(item.fileName);
      setEditedFileUrl(item.fileUrl);
    }
  };

  // Fetch initial categories and files on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const categoryResponse = await hitBeApi("categories?type=file", "GET");
        const fileResponse = await hitBeApi("files", "GET");

        if (categoryResponse.success) {
          setCategories(categoryResponse.data);
        }
        if (fileResponse.success) {
          setFiles(fileResponse.data);
        }
      } catch (error) {
        if (error.message.includes("Unauthorized")) {
          navigate("/");
          document.cookie =
            "token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
          toast({
            title: "Error",
            description: "Unauthorized: Invalid token, please login once",
            status: "error",
            duration: 2000,
            isClosable: true,
          });
          return;
        }
        toast({
          title: "Error",
          description: error.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    };
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle selecting a category
  const handleCategorySelect = async (
    category,
    isDirectClick = false,
    directClickIndex = 0
  ) => {
    if (isCategoryBeingSelected) {
      return;
    }
    setSelectedCategory(category);
    try {
      setIsCategoryBeingSelected(true);
      const subCategoryResponse = await hitBeApi(
        `categories?parentId=${category._id}`,
        "GET"
      );
      const subFileResponse = await hitBeApi(
        `files?categoryId=${category._id}`,
        "GET"
      );
      if (!isDirectClick) {
        setCategoryPath((categoryPath) => [...categoryPath, category]);
      } else {
        setCategoryPath((categoryPath) => [
          ...categoryPath.slice(0, directClickIndex + 1),
        ]);
      }
      if (subCategoryResponse.success) {
        setSubCategories(subCategoryResponse.data);
      }
      if (subFileResponse.success) {
        setSubFiles(subFileResponse.data);
      }
      setIsCategoryBeingSelected(false);
    } catch (error) {
      setIsCategoryBeingSelected(false);
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
    setSubCategories([]);
    setSubFiles([]);
    setCategoryPath([]);
  };

  // File upload handling
  const handleFileUpload = async (file, isUpdatedFile = false) => {
    if (isUploadingFile) {
      return;
    }
    const formData = new FormData();
    formData.append("file", file);

    try {
      const token = getCookieToken();
      setIsUploadingFile(true);
      const response = await axios.post(
        `${serverBaseUrl}/upload/aws`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            authorization: `Bearer ${token}`,
          },
        }
      );
      setIsUploadingFile(false);
      const uploadedFileURL = response.data.data.fileUrl;
      const uploadedFileName = response.data.data.fileName;
      if (isUpdatedFile) {
        setEditedFileUrl(uploadedFileURL); // Set the received file URL
        setEditedFileName(uploadedFileName);
      } else {
        setFileURL(uploadedFileURL); // Set the received file URL
        setFileName(uploadedFileName);
      }
      toast({
        title: "File Uploaded",
        description: "File uploaded successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleFileCreate = async () => {
    if (!fileURL) {
      return;
    }
    if (newTitle.length === 0) {
      toast({
        title: "Error",
        description: "File title required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    try {
      const createSubFileResponse = await hitBeApi(`files`, "POST", {
        fileUrl: fileURL, // Send the uploaded file URL
        categoryId: selectedCategory?._id,
        fileName: newTitle,
      });
      if (createSubFileResponse.success) {
        if (selectedCategory) {
          setSubFiles([...subFiles, createSubFileResponse.data]);
        } else {
          setFiles([...files, createSubFileResponse.data]);
        }
      }
      setNewTitle("");
      setFileURL(""); // Reset the file URL after creating
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleFileUpdate = async (fileId, updatedFileData) => {
    if (!fileId || !updatedFileData) {
      toast({
        title: "Error",
        description: "File ID and data are required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const updateFileResponse = await hitBeApi(
        `files/${fileId}`,
        "PUT",
        updatedFileData
      );

      if (updateFileResponse.success) {
        if (selectedCategory) {
          // Update the files list with the updated file information
          const updatedSubFiles = subFiles.map((file) =>
            file._id === fileId ? { ...file, ...updatedFileData } : file
          );
          setSubFiles(updatedSubFiles); // Update the state with new files list
          setIsEditing(null); // Exit editing mode
        } else {
          // Update the files list with the updated file information
          const updatedFiles = files.map((file) =>
            file._id === fileId ? { ...file, ...updatedFileData } : file
          );
          setFiles(updatedFiles); // Update the state with new files list
          setIsEditing(null); // Exit editing mode
        }

        toast({
          title: "Success",
          description: "File updated successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleFileDelete = async (fileId) => {
    if (!fileId) {
      toast({
        title: "Error",
        description: "File ID is required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const deleteFileResponse = await hitBeApi(`files/${fileId}`, "DELETE");

      if (deleteFileResponse.success) {
        // Filter out the deleted file from the files list
        const updatedFiles = files.filter((file) => file._id !== fileId);
        setFiles(updatedFiles); // Update the state with the new files list

        toast({
          title: "Success",
          description: "File deleted successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleCategoryCreate = async () => {
    if (newItem.length === 0) {
      return;
    }
    try {
      const createSubCategoryResponse = await hitBeApi(`categories`, "POST", {
        name: newItem,
        type: "file",
        parentId: selectedCategory?._id,
      });
      if (createSubCategoryResponse.success) {
        if (selectedCategory) {
          setSubCategories([...subCategories, createSubCategoryResponse.data]);
        } else {
          setCategories([...categories, createSubCategoryResponse.data]);
        }
      }
      setNewItem("");
      setNewTitle("");
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleCategoryUpdate = async (categoryId, updatedCategoryName) => {
    if (!categoryId || updatedCategoryName.length === 0) {
      toast({
        title: "Error",
        description: "Category ID and updated name are required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const updateCategoryResponse = await hitBeApi(
        `categories/${categoryId}`,
        "PUT",
        {
          name: updatedCategoryName,
          parentId: selectedCategory?._id, // Optional: if updating parent category
        }
      );

      if (updateCategoryResponse.success) {
        if (selectedCategory) {
          // Update the category in the list
          const updatedSubCategories = subCategories.map((category) =>
            category._id === categoryId
              ? { ...category, name: updatedCategoryName }
              : category
          );
          setSubCategories(updatedSubCategories);
        } else {
          // Update the category in the list
          const updatedCategories = categories.map((category) =>
            category._id === categoryId
              ? { ...category, name: updatedCategoryName }
              : category
          );
          setCategories(updatedCategories);
        }
        setIsEditing(null); // Exit editing mode

        toast({
          title: "Success",
          description: "Category updated successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleCategoryDelete = async (categoryId) => {
    if (!categoryId) {
      toast({
        title: "Error",
        description: "Category ID is required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const deleteCategoryResponse = await hitBeApi(
        `categories/${categoryId}`,
        "DELETE"
      );

      if (deleteCategoryResponse.success) {
        if (selectedCategory) {
          // Filter out the deleted category from the categories list
          const updatedSubCategories = subCategories.filter(
            (category) => category._id !== categoryId
          );
          setSubCategories(updatedSubCategories);
        } else {
          // Filter out the deleted category from the categories list
          const updatedCategories = categories.filter(
            (category) => category._id !== categoryId
          );
          setCategories(updatedCategories);
        }

        toast({
          title: "Success",
          description: "Category soft deleted successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Make API call to search URLs whenever searchTerm updates
  useEffect(() => {
    if (searchTerm && searchTerm.length > 0) {
      const fetchSearchResults = async () => {
        try {
          const response = await hitBeApi(`files/${searchTerm}`, "GET", null);
          setSearchResults(response.data); // Update the state with search results

          // Show success toast
          if (
            (response.data.length > 0 && searchResults.length === 0) ||
            (response.data.length === 0 && searchResults.length > 0)
          ) {
            toast({
              title: "Search completed.",
              description: `Found ${response.data.length} result(s) for "${searchTerm}".`,
              status: "success",
              duration: 4000,
              isClosable: true,
            });
          }
        } catch (error) {
          console.error("Error searching files:", error);
          // Show error toast
          toast({
            title: "Search failed.",
            description: error.message,
            status: "error",
            duration: 4000,
            isClosable: true,
          });
        }
      };

      fetchSearchResults();
    } else {
      setSearchResults([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, toast]); // Include toast in the dependency array

  const handleChangeSearchTerm = (newSearchTerm) => {
    setSearchTerm(newSearchTerm);
  };

  return (
    <Box minH="100vh" bg={pageBg} py={{ base: 4, md: 8 }}>
      <Container maxW="container.xl" px={{ base: 3, md: 6 }}>
        <VStack spacing={{ base: 5, md: 7 }} align="stretch">
          <Box
            role="region"
            aria-label="File library"
            overflow="hidden"
            borderRadius="2xl"
            boxShadow="lg"
            borderWidth="1px"
            borderColor={mutedLine}
            bg={surfaceCard}
          >
            <Box
              w="100%"
              minH={{ base: "120px", md: "auto" }}
              px={{ base: 5, md: 8 }}
              py={{ base: 6, md: 7 }}
              bg="orange.500"
              backgroundImage="linear-gradient(90deg, #ea580c 0%, #d97706 50%, #ca8a04 100%)"
            >
              <Flex
                align="flex-start"
                justify="space-between"
                gap={4}
                direction={{ base: "column", sm: "row" }}
              >
                <Flex
                  align="flex-start"
                  gap={4}
                  direction={{ base: "column", sm: "row" }}
                  flex="1"
                  minW={0}
                >
                  <Flex
                    align="center"
                    justify="center"
                    w={12}
                    h={12}
                    borderRadius="xl"
                    bg="whiteAlpha.300"
                    flexShrink={0}
                    borderWidth="1px"
                    borderColor="whiteAlpha.400"
                  >
                    <Icon as={FiFolder} boxSize={7} color="white" aria-hidden />
                  </Flex>
                  <Box textAlign="left" flex="1" minW={0}>
                    <Heading
                      as="h1"
                      size="lg"
                      color="white"
                      fontWeight="extrabold"
                      letterSpacing="tight"
                    >
                      File library
                    </Heading>
                    <Text
                      fontSize="sm"
                      color="white"
                      opacity={0.95}
                      mt={2}
                      maxW="3xl"
                      lineHeight="tall"
                    >
                      Organize uploads into folders, search by name, and open files in a
                      new tab. Root-level files and nested folders use the same patterns
                      as Portfolio and Splitwise.
                    </Text>
                  </Box>
                </Flex>
                <TabCacheButton tabId="files" onGradient />
              </Flex>
            </Box>
          </Box>

          <Card
            borderRadius="xl"
            borderWidth="1px"
            borderColor={mutedLine}
            bg={surfaceCard}
            boxShadow="sm"
            overflow="hidden"
          >
            <CardBody py={{ base: 3, md: 4 }}>
              <InputGroup size="lg">
                <InputLeftElement pointerEvents="none" h="100%">
                  <Icon as={FiSearch} color="gray.400" boxSize={5} aria-hidden />
                </InputLeftElement>
                <Input
                  pl={10}
                  placeholder="Search files by name…"
                  value={searchTerm}
                  onChange={(e) => handleChangeSearchTerm(e.target.value)}
                  borderRadius="full"
                  shadow="sm"
                  bg={inputBg}
                  borderColor={borderColor}
                  _hover={{ borderColor: hoverBorderColor }}
                  _focus={{
                    borderColor: focusBorderColor,
                    boxShadow: focusBoxShadow,
                  }}
                />
              </InputGroup>
            </CardBody>
          </Card>

          {categoryPath?.length > 0 && (
            <Flex
              align="center"
              flexWrap="wrap"
              gap={1}
              px={4}
              py={3}
              borderRadius="xl"
              borderWidth="1px"
              borderColor={mutedLine}
              bg={breadcrumbBg}
              boxShadow="sm"
            >
              <Text fontSize="xs" fontWeight="semibold" color={subtextColor} mr={2}>
                Path
              </Text>
              <Stack spacing={2} direction="row" alignItems="center" flexWrap="wrap">
                {categoryPath?.map((category, index) => (
                  <Box key={category._id || index} position="relative" display="inline-flex" alignItems="center">
                    <Text
                      fontWeight="semibold"
                      fontSize="sm"
                      color={index === 0 ? categoryFolderAccent : categoryNestedAccent}
                      px={1}
                      _hover={{ textDecoration: "underline", cursor: "pointer" }}
                      onClick={() => handleCategorySelect(category, true, index)}
                    >
                      {category.name}
                    </Text>
                    {index < categoryPath.length - 1 && (
                      <Text as="span" fontWeight="bold" color={subtextColor} mx={1.5}>
                        /
                      </Text>
                    )}
                  </Box>
                ))}
              </Stack>
            </Flex>
          )}

          {searchResults.length > 0 && (
            <Card
              borderRadius="xl"
              borderWidth="1px"
              borderColor={mutedLine}
              bg={searchPanelBg}
              boxShadow="md"
              overflow="hidden"
            >
              <CardBody p={{ base: 4, md: 5 }}>
                <Flex align="center" justify="space-between" mb={4} gap={3} flexWrap="wrap">
                  <Heading size="md" color={headingCategoryColor}>
                    Search results
                  </Heading>
                  <IconButton
                    icon={<CloseIcon />}
                    aria-label="Clear search results"
                    variant="outline"
                    colorScheme="red"
                    size="sm"
                    borderRadius="full"
                    onClick={() => {
                      setSearchResults([]);
                      setSearchTerm("");
                    }}
                  />
                </Flex>
                <VStack spacing={3} align="stretch" maxH={{ base: "50vh", md: "min(55vh, 400px)" }} overflowY="auto">
                  {searchResults.map((result) => (
                    <a
                      key={result._id}
                      href={result.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: "block", width: "100%", textDecoration: "none" }}
                    >
                      <Box
                        p={4}
                        bg={surfaceCard}
                        borderWidth="1px"
                        borderColor={mutedLine}
                        borderRadius="lg"
                        w="full"
                        shadow="sm"
                        transition="all 0.2s ease"
                        _hover={{
                          shadow: "md",
                          borderColor: "orange.300",
                          transform: "translateY(-1px)",
                        }}
                      >
                        <Text fontWeight="bold" fontSize="md" color={linkColor}>
                          {result.fileName}
                        </Text>
                        <Text fontSize="xs" color={subtextColor} noOfLines={2} wordBreak="break-all" mt={1}>
                          {result.fileUrl}
                        </Text>
                      </Box>
                    </a>
                  ))}
                </VStack>
              </CardBody>
            </Card>
          )}

          {!selectedCategory ? (
        <VStack spacing={6} align="stretch">
          {categories.length > 0 && (
            <DashboardCategoryPanel
              variant="browse"
              title="Your folders"
              description={`${categories.length} top-level folder${
                categories.length === 1 ? "" : "s"
              } — open one to manage files or subfolders.`}
            >
              <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
                {categories.map((category) => (
                  <DashboardFolderCard
                    key={category._id}
                    itemId={category._id}
                    variant="teal"
                    title={category.name}
                    borderColor={tableStyles.borderColor}
                    textColor={textColorCard}
                    inputBgColor={inputBg}
                    categoryRowBg={categoryRowBg}
                    categoryRowHoverBg={categoryRowHoverBg}
                    categoryIconBg={categoryIconBgRoot}
                    isEditingThisRow={
                      isEditing?.id === category._id &&
                      isEditing.type === "category"
                    }
                    editedTitle={editedCategoryName}
                    onEditedTitleChange={setEditedCategoryName}
                    onSelect={() => handleCategorySelect(category)}
                    onSaveEdit={() =>
                      handleCategoryUpdate(category._id, editedCategoryName)
                    }
                    onStartEdit={() => handleStartEdit(category, "category")}
                    onDelete={handleCategoryDelete}
                    deleteAriaLabel="Delete folder"
                    collaborationShared={!!category.collaborationShared}
                    collaborationCanInvite={!!category.collaborationCanInvite}
                    showShareButton={
                      !category.collaborationShared || !!category.collaborationCanInvite
                    }
                    onShareClick={() => {
                      setShareCategoryTarget(category);
                      shareFolderModal.onOpen();
                    }}
                  />
                ))}
              </SimpleGrid>
            </DashboardCategoryPanel>
          )}

          {files.length > 0 && (
            <DashboardDataTable
              variant="emphasis"
              title="Files at library root"
              description={`${files.length} file${
                files.length === 1 ? "" : "s"
              } not in a folder — hover a row for emphasis.`}
              maxH={{ base: "none", md: "min(55vh, 480px)" }}
            >
              <Table
                variant="simple"
                size="sm"
                colorScheme="gray"
                sx={{ borderCollapse: "separate", borderSpacing: 0 }}
              >
                <Thead
                  position="sticky"
                  top={0}
                  zIndex={1}
                  bg={tableStyles.tableHeaderBg}
                  backdropFilter="blur(8px)"
                  borderBottomWidth="1px"
                  borderColor={tableStyles.borderColor}
                >
                  <Tr>
                    <Th {...tableStyles.thProps}>Name</Th>
                    <Th {...tableStyles.thProps}>Link</Th>
                    <Th {...tableStyles.thProps} textAlign="right" w="120px">
                      Actions
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {files.map((file) => (
                    <Tr key={file._id} sx={tableStyles.trHoverSx}>
                      <Td py={3} maxW="240px">
                        {isEditing?.id === file._id &&
                        isEditing.type === "file" ? (
                          <Input
                            value={editedFileName}
                            onChange={(e) => setEditedFileName(e.target.value)}
                            size="sm"
                            bg={inputBg}
                          />
                        ) : (
                          <Button
                            as="a"
                            href={file.fileUrl}
                            target="_blank"
                            variant="link"
                            color={linkColor}
                            _hover={{ color: hoverLinkColor }}
                            fontSize="sm"
                            fontWeight="semibold"
                            justifyContent="flex-start"
                            whiteSpace="normal"
                            textAlign="left"
                            h="auto"
                            py={1}
                          >
                            {file.fileName}
                          </Button>
                        )}
                      </Td>
                      <Td py={3} fontSize="xs" color="gray.500" maxW="280px">
                        {isEditing?.id === file._id &&
                        isEditing.type === "file" ? (
                          <Input
                            type="file"
                            onChange={(e) =>
                              handleFileUpload(e.target.files[0], true)
                            }
                            size="sm"
                            p={1}
                          />
                        ) : (
                          <Text noOfLines={2} wordBreak="break-all">
                            {file.fileUrl}
                          </Text>
                        )}
                      </Td>
                      <Td textAlign="right" py={3}>
                        <HStack spacing={1} justify="flex-end">
                          {isEditing?.id === file._id &&
                          isEditing.type === "file" ? (
                            <IconButton
                              icon={<CheckIcon />}
                              size="sm"
                              isDisabled={isUploadingFile}
                              onClick={() =>
                                handleFileUpdate(file._id, {
                                  fileName: editedFileName,
                                  fileUrl: editedFileUrl,
                                })
                              }
                              colorScheme="green"
                              aria-label="Confirm Edit"
                            />
                          ) : (
                            <IconButton
                              icon={<EditIcon />}
                              size="sm"
                              onClick={() => handleStartEdit(file, "file")}
                              colorScheme="blue"
                              aria-label="Edit File"
                            />
                          )}
                          <DeleteButtonWithConfirmation
                            onDelete={handleFileDelete}
                            itemId={file._id}
                            size="sm"
                            colorScheme="red"
                            ariaLabel="Delete File"
                          />
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </DashboardDataTable>
          )}

          {categories.length === 0 && files.length === 0 && (
            <Card
              borderRadius="xl"
              borderWidth="1px"
              borderStyle="dashed"
              borderColor={mutedLine}
              bg={breadcrumbBg}
            >
              <CardBody py={10} px={6} textAlign="center">
                <Icon as={FiFolder} boxSize={10} color="orange.400" mx="auto" mb={3} />
                <Heading size="md" color={textColorCard} mb={2}>
                  Your library is empty
                </Heading>
                <Text fontSize="sm" color={subtextColor} maxW="md" mx="auto">
                  Create a folder or upload a file using the panels at the bottom of this
                  page. You can nest folders and keep root-level files without a folder.
                </Text>
              </CardBody>
            </Card>
          )}
        </VStack>
      ) : (
        <>
          <Card
            borderRadius="xl"
            borderWidth="1px"
            borderColor={mutedLine}
            bg={surfaceCard}
            boxShadow="sm"
            overflow="hidden"
          >
            <CardBody py={{ base: 4, md: 5 }}>
              <Flex
                justify="space-between"
                align="flex-start"
                gap={4}
                flexWrap="wrap"
              >
                <Box flex="1" minW={0}>
                  <Heading
                    as="h2"
                    size="lg"
                    color={headingCategoryColor}
                    fontWeight="bold"
                    lineHeight="short"
                  >
                    {selectedCategory.name}
                  </Heading>
                  <Text fontSize="sm" color={subtextColor} mt={2} maxW="lg">
                    Subfolders and files in this category. Use the path above to go back.
                  </Text>
                </Box>
                <IconButton
                  icon={<CloseIcon />}
                  size="md"
                  colorScheme="red"
                  flexShrink={0}
                  borderRadius="full"
                  onClick={handleBackToCategories}
                  aria-label="Back to folders"
                  shadow="sm"
                />
              </Flex>
            </CardBody>
          </Card>

          <VStack spacing={6} align="stretch" w="full">
            {subCategories.length > 0 && (
              <DashboardCategoryPanel
                variant="nested"
                title="Subfolders"
                description={`${subCategories.length} subfolder${
                  subCategories.length === 1 ? "" : "s"
                } under “${selectedCategory.name}”.`}
              >
                <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
                  {subCategories.map((subcategory) => (
                    <DashboardFolderCard
                      key={subcategory._id}
                      itemId={subcategory._id}
                      variant="blue"
                      title={subcategory.name}
                      borderColor={tableStyles.borderColor}
                      textColor={textColorCard}
                      inputBgColor={inputBg}
                      categoryRowBg={categoryRowBg}
                      categoryRowHoverBg={categoryRowHoverBg}
                      categoryIconBg={categoryIconBgNested}
                      isEditingThisRow={
                        isEditing?.id === subcategory._id &&
                        isEditing.type === "category"
                      }
                      editedTitle={editedCategoryName}
                      onEditedTitleChange={setEditedCategoryName}
                      onSelect={() => handleCategorySelect(subcategory)}
                      onSaveEdit={() =>
                        handleCategoryUpdate(subcategory._id, editedCategoryName)
                      }
                      onStartEdit={() =>
                        handleStartEdit(subcategory, "category")
                      }
                      onDelete={handleCategoryDelete}
                      deleteAriaLabel="Delete subfolder"
                    />
                  ))}
                </SimpleGrid>
              </DashboardCategoryPanel>
            )}

            {subFiles.length > 0 ? (
              <DashboardDataTable
                variant="emphasis"
                title="Files in this folder"
                description={`${subFiles.length} file${
                  subFiles.length === 1 ? "" : "s"
                } — hover a row for emphasis.`}
                maxH={{ base: "none", md: "min(55vh, 480px)" }}
              >
                <Table
                  variant="simple"
                  size="sm"
                  colorScheme="gray"
                  sx={{ borderCollapse: "separate", borderSpacing: 0 }}
                >
                  <Thead
                    position="sticky"
                    top={0}
                    zIndex={1}
                    bg={tableStyles.tableHeaderBg}
                    backdropFilter="blur(8px)"
                    borderBottomWidth="1px"
                    borderColor={tableStyles.borderColor}
                  >
                    <Tr>
                      <Th {...tableStyles.thProps}>Name</Th>
                      <Th {...tableStyles.thProps}>Link</Th>
                      <Th {...tableStyles.thProps} textAlign="right" w="120px">
                        Actions
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {subFiles.map((file) => (
                      <Tr key={file._id} sx={tableStyles.trHoverSx}>
                        <Td py={3} maxW="240px">
                          {isEditing?.id === file._id &&
                          isEditing.type === "file" ? (
                            <Input
                              value={editedFileName}
                              onChange={(e) => setEditedFileName(e.target.value)}
                              size="sm"
                              bg={inputBg}
                            />
                          ) : (
                            <Button
                              as="a"
                              href={file.fileUrl}
                              target="_blank"
                              variant="link"
                              color={linkColor}
                              _hover={{ color: hoverLinkColor }}
                              fontSize="sm"
                              fontWeight="semibold"
                              justifyContent="flex-start"
                              whiteSpace="normal"
                              textAlign="left"
                              h="auto"
                              py={1}
                            >
                              {file.fileName}
                            </Button>
                          )}
                        </Td>
                        <Td py={3} fontSize="xs" color="gray.500" maxW="280px">
                          {isEditing?.id === file._id &&
                          isEditing.type === "file" ? (
                            <Input
                              type="file"
                              onChange={(e) =>
                                handleFileUpload(e.target.files[0], true)
                              }
                              size="sm"
                              p={1}
                            />
                          ) : (
                            <Text noOfLines={2} wordBreak="break-all">
                              {file.fileUrl}
                            </Text>
                          )}
                        </Td>
                        <Td textAlign="right" py={3}>
                          <HStack spacing={1} justify="flex-end">
                            {isEditing?.id === file._id &&
                            isEditing.type === "file" ? (
                              <IconButton
                                icon={<CheckIcon />}
                                size="sm"
                                isDisabled={isUploadingFile}
                                onClick={() =>
                                  handleFileUpdate(file._id, {
                                    fileName: editedFileName,
                                    fileUrl: editedFileUrl,
                                  })
                                }
                                colorScheme="green"
                                aria-label="Confirm Edit"
                              />
                            ) : (
                              <IconButton
                                icon={<EditIcon />}
                                size="sm"
                                onClick={() => handleStartEdit(file, "file")}
                                colorScheme="blue"
                                aria-label="Edit File"
                              />
                            )}
                            <DeleteButtonWithConfirmation
                              onDelete={handleFileDelete}
                              itemId={file._id}
                              size="sm"
                              colorScheme="red"
                              ariaLabel="Delete File"
                            />
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </DashboardDataTable>
            ) : (
              subCategories.length === 0 && (
                <Text color={subtextColor} fontSize="sm">
                  No subfolders or files in this category yet.
                </Text>
              )
            )}
          </VStack>
        </>
      )}

      <Flex
        gap={{ base: 6, md: 8 }}
        align="stretch"
        flexDirection={{ base: "column", md: "row" }}
        justify="space-between"
        w="full"
      >
        <DashboardActionShell
          kind="file"
          badgeLabel="Upload"
          title="Add a file"
          flex={1}
        >
          <VStack spacing={4} align="stretch">
            <Box
              as="label"
              htmlFor="file-upload"
              cursor="pointer"
              border="2px dashed"
              borderColor="orange.300"
              borderRadius="lg"
              p={6}
              textAlign="center"
              bg={uploadBgColor}
              _hover={{ bg: uploadHoverBgColor }}
              transition="background 0.2s"
            >
              <Icon as={MdCloudUpload} boxSize={10} color="orange.400" />
              <Text fontSize="sm" mt={3} fontWeight="bold" color={uploadTextColor}>
                Choose file
              </Text>
              {fileURL && fileURL.length > 0 && (
                <Text fontSize="xs" mt={2}>
                  Selected: {fileName}
                </Text>
              )}
            </Box>
            <Input
              id="file-upload"
              type="file"
              onChange={(e) => handleFileUpload(e.target.files[0])}
              display="none"
            />
            <Input
              placeholder="Display name for this file"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              borderRadius="md"
              size="md"
              bg={boxBgColor}
              _focus={{
                borderColor: inputFocusBorderColor,
                boxShadow: inputFocusBoxShadow,
              }}
            />
            <Button
              onClick={handleFileCreate}
              colorScheme="orange"
              size="md"
              isDisabled={isUploadingFile}
              leftIcon={<AddIcon />}
            >
              Save file
            </Button>
          </VStack>
        </DashboardActionShell>

        <DashboardActionShell
          kind="folder"
          badgeLabel="Folders"
          title="Add a folder"
          flex={1}
        >
          <VStack spacing={4} align="stretch">
            <Input
              placeholder="Folder name"
              value={newItem}
              onChange={(e) => setNewItem(e.target.value)}
              borderRadius="md"
              size="md"
              bg={boxBgColor}
              _focus={{
                borderColor: inputFocusBorderColor,
                boxShadow: inputFocusBoxShadow,
              }}
            />
            <Button onClick={handleCategoryCreate} colorScheme="green" size="md">
              Create folder
            </Button>
          </VStack>
        </DashboardActionShell>
      </Flex>
        </VStack>
      </Container>

      <ShareCollaborationModal
        isOpen={shareFolderModal.isOpen}
        onClose={() => {
          shareFolderModal.onClose();
          setShareCategoryTarget(null);
        }}
        titleLabel={
          shareCategoryTarget
            ? `Share files folder “${shareCategoryTarget.name}”`
            : "Share folder"
        }
        kind="FILE_CATEGORY"
        anchor={{
          categoryAnchorId: shareCategoryTarget
            ? String(shareCategoryTarget._id)
            : undefined,
        }}
      />
    </Box>
  );
};

export default FileManager;
